import { computed, effect } from '@preact/signals';

import type { HouseholdTypeValue, ThemePackValue } from '@amumin/api-client';
import { isSharedHouseType, playfulArt, WORD_SET_OF } from '@amumin/domain/modules';

import { fallbackType, moduleOn } from './modules.js';
import { appText, household } from './session.js';
import { personalTheme } from './personalTheme.js';

// Soort huishouden in de ouderapp (golf 6b, 0043_huishoudtypes): het thema (tokens op <html>), de
// woorden per soort en of er punten te zien zijn. De server is de bron (GET /v1/app/household);
// zolang die er niet is, geldt het gezin: niemand ziet dan iets anders dan vóór 0043.

export const householdType = computed<HouseholdTypeValue>(
  () => household.value?.household_type ?? 'family',
);

export const themePack = computed<ThemePackValue>(
  () => personalTheme.value ?? household.value?.theme ?? 'family-warm',
);

/** Een huis met huisgenoten (huisgenoten of studentenhuis): rooster en bord in de gang. */
export const sharedHouse = computed(() => isSharedHouseType(householdType.value));

/**
 * De woorden van deze soort ("Huisgenoten", "Rooster", "Te doen"); "alleen" in het enkelvoud, een
 * woongroep met "Bewoners" (live r1).
 */
export const houseWords = computed(() => {
  const h = appText.value.households;
  const words = h.words[WORD_SET_OF[householdType.value]];
  if (householdType.value === 'solo') return { ...words, ...h.soloWords };
  if (householdType.value === 'cohousing') return { ...words, ...h.cohousingWords };
  return words;
});

/**
 * Toon per soort (fixer golf 6b r1, founder-ux): de teksten die bij een gezin over kinderen,
 * gezichtjes en klusjes gaan, rustig en volwassen voor stel, alleen, huisgenoten en zorg. null bij
 * een gezin: dan gelden de gewone teksten.
 */
export const tone = computed(() => {
  const type = householdType.value;
  const t = appText.value.households.tone;
  if (type === 'solo') return t.solo;
  const set = WORD_SET_OF[type];
  return set === 'family' ? null : t[set];
});

/** Is dit een gezin (met kinderen, sterren en brengen en halen)? */
export const familyLike = computed(() => playfulArt(householdType.value));

/** Punten en sterren tonen? Alleen met de module Beloningen (golf 6b). */
export function pointsShown(): boolean {
  return moduleOn('rewards');
}

/**
 * Zet het thema op <html>: `data-theme-pack` (geen attribuut voor het warme gezinsthema),
 * `data-art` (full bij gezinnen, none bij huisgenoten, sparse bij de rest) en `data-rewards`
 * (off = elke PointsBadge weg, ook op plekken die de module zelf niet kennen). Geeft een
 * opruimfunctie terug.
 */
export function installHouseholdTheme(): () => void {
  return effect(() => {
    fallbackType.value = household.value?.household_type ?? null;
    const root = document.documentElement;
    const pack = themePack.value;
    if (pack === 'family-warm') root.removeAttribute('data-theme-pack');
    else root.setAttribute('data-theme-pack', pack);
    const type = householdType.value;
    const art = personalTheme.value
      ? pack === 'family-warm'
        ? 'full'
        : pack === 'shared-graphic'
          ? 'none'
          : 'sparse'
      : playfulArt(type)
        ? 'full'
        : isSharedHouseType(type)
          ? 'none'
          : 'sparse';
    if (art === 'full') root.removeAttribute('data-art');
    else root.setAttribute('data-art', art);
    if (moduleOn('rewards')) root.removeAttribute('data-rewards');
    else root.setAttribute('data-rewards', 'off');
  });
}

/** "Ouder" bij een gezin; "Huisgenoot" of "Volwassene" bij de andere soorten (lidsoort adult). */
export function adultLabel(): string {
  return familyLike.value ? appText.value.members.kindAdult : houseWords.value.adult;
}
